/**
 * KeptKidPicker — "who keeps creating" picker for a seat-reducing downgrade
 * (e.g. Family 3 seats → Single 1 seat). Recreated from the design handoff's
 * ``rc_kept_kid_picker.jsx``.
 *
 * Slots into ManageSubscriptionModal's "Change plan" section, BELOW the
 * existing one-line "what happens" warning. Pick exactly ``keepCount`` ids;
 * anyone (parent, co-parent, kid) can hold a creating seat, and a grown-up
 * chooses. Members arrive ordered by ``seats.ts``: the account holder first,
 * then kids by when they were added, which is also the default choice. Members on their
 * own separate subscription render in a non-selectable "unaffected" group and
 * are excluded from the seat math entirely.
 *
 * Non-selected members are NEVER deleted — projects, conversations, avatars
 * stay. They just move to Explore mode until the parent upgrades again; the
 * sky-tinted reassurance below the list says so explicitly.
 *
 * Selection model: order-tracked swap. Tapping a member when the picker is
 * full drops the earliest-picked id and adds the new one — a tap always does
 * something visible (for ``keepCount === 1`` this is plain radio behavior).
 */
import { Icon } from "../chrome/Icon";
import { ChromeTokens } from "../chrome/tokens";

export type KeptPickerRole = "parent" | "kid";

export interface KeptPickerMember {
  id: string;
  name: string;
  role: KeptPickerRole;
  /** Decorative tint key, drives the avatar ring. */
  tint: "sky" | "coral" | "lavender" | "grass" | "sand";
  /** True for the current account holder ("You"). */
  you?: boolean;
  /** Real saved avatar thumbnail URL. Falls back to inline silhouette. */
  avatarUrl?: string | null;
  /** Member is on their own separate subscription → unaffected by this change. */
  ownPlan?: boolean;
}

interface KeptKidPickerProps {
  members: KeptPickerMember[];
  /** Exactly this many members must be kept. */
  keepCount: number;
  /** Currently kept ids (order matters for the swap behavior). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Display name of the target plan, used in the prompt copy. */
  targetPlanName?: string;
}

function MemberAvatar({ member, dimmed }: { member: KeptPickerMember; dimmed?: boolean }): JSX.Element {
  const ring = `color-mix(in srgb, var(--chrome-color-${member.tint}) 55%, var(--chrome-color-base))`;
  return (
    <span
      style={{
        position: "relative",
        flexShrink: 0,
        width: 46,
        height: 46,
        borderRadius: ChromeTokens.radius.pill,
        background: ring,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        transition: "filter 200ms cubic-bezier(0.22, 0.9, 0.3, 1)",
        filter: dimmed ? "saturate(0.55)" : "none",
      }}
    >
      {member.avatarUrl ? (
        <img
          src={member.avatarUrl}
          alt=""
          width={46}
          height={46}
          style={{ width: 46, height: 46, objectFit: "cover", opacity: dimmed ? 0.7 : 1 }}
        />
      ) : (
        <svg viewBox="0 0 46 46" width={46} height={46} aria-hidden="true" style={{ opacity: dimmed ? 0.5 : 0.78 }}>
          <circle cx="23" cy="17" r="9" fill={ChromeTokens.color.ink} />
          <path d="M6 46c0-10 7.6-15 17-15s17 5 17 15Z" fill={ChromeTokens.color.ink} />
        </svg>
      )}
    </span>
  );
}

function roleTag(m: KeptPickerMember): string | null {
  if (m.you) return "You";
  if (m.role === "parent") return "Parent";
  return null;
}

export function KeptKidPicker({
  members,
  keepCount,
  value,
  onChange,
  targetPlanName,
}: KeptKidPickerProps): JSX.Element {
  const onPlan = members.filter((m) => !m.ownPlan);
  const separate = members.filter((m) => m.ownPlan);

  const isKept = (id: string) => value.includes(id);
  const done = value.length === keepCount;

  function toggle(id: string): void {
    if (isKept(id)) {
      onChange(value.filter((x) => x !== id));
    } else if (value.length < keepCount) {
      onChange([...value, id]);
    } else {
      // Full → drop the earliest, swap the new one in.
      onChange([...value.slice(1), id]);
    }
  }

  const planLabel = targetPlanName || (keepCount === 1 ? "Single" : "new");
  const peopleWord = keepCount === 1 ? "person" : "people";

  return (
    <div style={{ marginTop: ChromeTokens.spacing.md }}>
      {/* prompt + live progress chip */}
      <div style={{ display: "flex", alignItems: "center", gap: ChromeTokens.spacing.sm + 2, marginBottom: ChromeTokens.spacing.xs }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>Choose who keeps creating</div>
        <span
          aria-live="polite"
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 800,
            padding: "4px 11px",
            borderRadius: ChromeTokens.radius.pill,
            color: done ? "#3a6e3f" : ChromeTokens.color.ink,
            background: done
              ? `color-mix(in srgb, var(--chrome-color-grass) 34%, var(--chrome-color-base))`
              : ChromeTokens.color.baseMuted,
            transition: "background 200ms cubic-bezier(0.22, 0.9, 0.3, 1)",
          }}
        >
          {done && <Icon name="check" size={13} stroke="#3a6e3f" />}
          Pick {value.length} of {keepCount}
        </span>
      </div>
      <p style={{ fontSize: 12.5, color: ChromeTokens.color.inkMuted, lineHeight: 1.45, margin: `0 0 ${ChromeTokens.spacing.md}px` }}>
        Your {planLabel} plan has room for {keepCount} {peopleWord} to make new things, and you choose who:
        yourself or any of your kids. Everyone else keeps everything they made and can still play it. You can
        change this anytime.
      </p>

      {/* the pickable members (covered by this plan) */}
      <div role="group" aria-label="Choose who keeps creating" style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {onPlan.map((m) => {
          const kept = isKept(m.id);
          const tag = roleTag(m);
          return (
            <button
              key={m.id}
              type="button"
              role="checkbox"
              aria-checked={kept}
              onClick={() => toggle(m.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 13,
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                padding: "11px 13px",
                borderRadius: ChromeTokens.radius.md,
                fontFamily: ChromeTokens.font.ui,
                background: kept
                  ? `color-mix(in srgb, var(--chrome-color-sun) 18%, var(--chrome-color-base))`
                  : ChromeTokens.color.base,
                border: kept
                  ? `2.5px solid ${ChromeTokens.color.sun}`
                  : `2px solid ${ChromeTokens.color.baseMuted}`,
                boxShadow: kept ? ChromeTokens.shadow.soft : "none",
                transition:
                  "border-color 160ms cubic-bezier(0.22, 0.9, 0.3, 1), background 160ms cubic-bezier(0.22, 0.9, 0.3, 1), box-shadow 160ms cubic-bezier(0.22, 0.9, 0.3, 1)",
                minHeight: 44,
              }}
            >
              <MemberAvatar member={m} dimmed={!kept} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: ChromeTokens.font.display, fontSize: 19, lineHeight: 1.1, color: ChromeTokens.color.ink }}>
                    {m.name}
                  </span>
                  {tag && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: ".04em",
                        textTransform: "uppercase",
                        color: ChromeTokens.color.inkMuted,
                        background: ChromeTokens.color.baseMuted,
                        padding: "2px 7px",
                        borderRadius: ChromeTokens.radius.pill,
                      }}
                    >
                      {tag}
                    </span>
                  )}
                </span>
                {kept ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 800, color: ChromeTokens.color.clay, marginTop: 2 }}>
                    <Icon name="wand-sparkles" size={13} stroke={ChromeTokens.color.clay} /> Keeps creating
                  </span>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: ChromeTokens.color.inkMuted, marginTop: 2 }}>
                    <Icon name="eye" size={13} stroke={ChromeTokens.color.inkMuted} /> Explore mode · keeps everything
                  </span>
                )}
              </span>
              <span
                aria-hidden="true"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: ChromeTokens.radius.pill,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  border: kept ? "none" : `2px solid ${ChromeTokens.color.baseMuted}`,
                  background: kept ? ChromeTokens.color.sun : "transparent",
                }}
              >
                {kept && <Icon name="check" size={14} stroke={ChromeTokens.color.ink} />}
              </span>
            </button>
          );
        })}
      </div>

      {/* Members on their OWN plan — never selectable, never at risk. */}
      {separate.length > 0 && (
        <div style={{ marginTop: ChromeTokens.spacing.md }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: ChromeTokens.color.inkMuted,
              marginBottom: 7,
            }}
          >
            On their own plan — not affected
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {separate.map((m) => {
              const tag = roleTag(m);
              return (
                <div
                  key={m.id}
                  aria-disabled="true"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 13,
                    padding: "11px 13px",
                    borderRadius: ChromeTokens.radius.md,
                    background: `color-mix(in srgb, var(--chrome-color-grass) 14%, var(--chrome-color-base))`,
                    border: `1.5px dashed color-mix(in srgb, var(--chrome-color-grass) 55%, var(--chrome-color-base-muted))`,
                  }}
                >
                  <MemberAvatar member={m} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontFamily: ChromeTokens.font.display, fontSize: 19, lineHeight: 1.1, color: ChromeTokens.color.ink }}>
                        {m.name}
                      </span>
                      {tag && (
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: ".04em",
                            textTransform: "uppercase",
                            color: ChromeTokens.color.inkMuted,
                            background: ChromeTokens.color.baseMuted,
                            padding: "2px 7px",
                            borderRadius: ChromeTokens.radius.pill,
                          }}
                        >
                          {tag}
                        </span>
                      )}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#3a6e3f", marginTop: 2 }}>
                      <Icon name="check" size={13} stroke="#3a6e3f" /> Keeps creating on their own subscription
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* the reassurance — stated prominently; anxiety reduction is the point */}
      <div
        style={{
          display: "flex",
          gap: ChromeTokens.spacing.sm + 2,
          alignItems: "flex-start",
          marginTop: ChromeTokens.spacing.md,
          padding: "12px 13px",
          borderRadius: ChromeTokens.radius.md,
          background: `color-mix(in srgb, var(--chrome-color-sky) 26%, var(--chrome-color-base))`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: ChromeTokens.radius.pill,
            flexShrink: 0,
            marginTop: 1,
            background: ChromeTokens.color.base,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon name="shield" size={15} stroke={ChromeTokens.color.clay} />
        </span>
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          <strong>Nobody loses anything.</strong> Everyone keeps all their projects, conversations and avatars exactly
          as they are. Whoever you don't pick here just switches to Explore mode on this plan — they can still play and
          explore everything, they just can't make new things until you upgrade again.
          {separate.length > 0 && <> Anyone with their own separate plan isn't affected at all.</>} It's fully
          reversible.
        </div>
      </div>
    </div>
  );
}

export default KeptKidPicker;
